import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import * as API from '../services/api';
import {
  type AnalysisDocument,
  type FeedbackItem,
  type Notification,
} from '../services/api';

// Re-export types from the API layer so the rest of the app doesn't need to
// change its imports.
export type { AnalysisDocument, FeedbackItem, Notification };
export type { UserInfo } from '../services/api';

// Local-only types that don't need a backend equivalent
export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Keep these type aliases so existing pages compile without change
export type DocumentStatus = 'processing' | 'completed' | 'failed';
export type QualityRating = 'satisfactory' | 'needs_improvement' | 'unsatisfactory';
export type FeedbackStatus = 'open' | 'in_review' | 'resolved';

interface FilterState {
  search: string;
  status: DocumentStatus | 'all';
  quality: QualityRating | 'all';
  dateRange: 'today' | 'week' | 'month' | 'all';
}

interface AppState {
  // Auth
  isAuthenticated: boolean;
  user: API.UserInfo | null;
  authScreen: 'login' | 'logout' | 'app';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  completeLogout: () => void;

  // Navigation
  activeView: string;
  setActiveView: (view: string) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Documents
  documents: AnalysisDocument[];
  selectedDocumentId: string | null;
  setSelectedDocumentId: (id: string | null) => void;
  navigateToDocument: (documentId: string) => void;
  addDocument: (file: File, docName: string, templateVersion: string, templateFile?: File) => Promise<string>;
  refreshDocuments: () => Promise<void>;
  deleteDocument: (id: string) => void;
  renameDocument: (id: string, newName: string) => void;
  toggleBookmark: (id: string) => void;
  archiveDocument: (id: string) => void;
  bookmarkedIds: Set<string>;
  archivedIds: Set<string>;

  // Filters
  filters: FilterState;
  setFilters: (filters: Partial<FilterState>) => void;
  filteredDocuments: AnalysisDocument[];

  // Feedback
  feedbackItems: FeedbackItem[];
  updateFeedbackStatus: (id: string, status: FeedbackStatus) => void;
  applyAction: (id: string) => void;
  dismissFeedback: (id: string) => void;

  // Notifications
  notifications: Notification[];
  unreadCount: number;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  showNotifications: boolean;
  setShowNotifications: (show: boolean) => void;

  // User menu
  showUserMenu: boolean;
  setShowUserMenu: (show: boolean) => void;

  // Toasts
  toasts: Toast[];
  addToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;

  // Upload modal
  showUploadModal: boolean;
  setShowUploadModal: (show: boolean) => void;

  // API status
  backendConnected: boolean;
}

const AppContext = createContext<AppState | null>(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<API.UserInfo | null>(null);
  const [authScreen, setAuthScreen] = useState<'login' | 'logout' | 'app'>('login');
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [documents, setDocuments] = useState<AnalysisDocument[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [backendConnected, setBackendConnected] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [archivedIds, setArchivedIds] = useState<Set<string>>(new Set());
  const [filters, setFiltersState] = useState<FilterState>({
    search: '',
    status: 'all',
    quality: 'all',
    dateRange: 'all',
  });

  // Track polling intervals so we can clear them
  const pollingRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // ── Toast system ───────────────────────────────────────────────
  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = `toast-${Date.now()}`;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Backend connectivity check ─────────────────────────────────
  useEffect(() => {
    API.checkHealth()
      .then(() => setBackendConnected(true))
      .catch(() => {
        setBackendConnected(false);
        addToast('Backend not reachable. Running in offline mode with mock data.', 'error');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load data once authenticated ───────────────────────────────
  const refreshDocuments = useCallback(async () => {
    try {
      const docs = await API.listDocuments();
      setDocuments(docs);
    } catch {
      // silently keep previous state
    }
  }, []);

  const refreshFeedback = useCallback(async () => {
    try {
      const items = await API.listFeedback();
      setFeedbackItems(items);
    } catch { }
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      const items = await API.listNotifications();
      setNotifications(items);
    } catch { }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !backendConnected) return;
    refreshDocuments();
    refreshFeedback();
    refreshNotifications();

    // Poll notifications every 30 s
    const notifInterval = setInterval(refreshNotifications, 30_000);
    return () => clearInterval(notifInterval);
  }, [isAuthenticated, backendConnected, refreshDocuments, refreshFeedback, refreshNotifications]);

  // ── Document polling (while processing) ───────────────────────
  const startPolling = useCallback((docId: string) => {
    if (pollingRef.current.has(docId)) return;
    const interval = setInterval(async () => {
      try {
        const status = await API.getDocumentStatus(docId);
        setDocuments(prev => prev.map(d =>
          d.id === docId
            ? {
              ...d,
              status: status.status as DocumentStatus,
              processingProgress: status.processingProgress,
              qualityRating: (status.qualityRating as QualityRating | undefined),
              issuesDetected: status.issuesDetected,
            }
            : d
        ));

        if (status.status !== 'processing') {
          clearInterval(interval);
          pollingRef.current.delete(docId);
          // Fetch full doc with findings
          const full = await API.getDocument(docId);
          setDocuments(prev => prev.map(d => (d.id === docId ? full : d)));
          await refreshNotifications();
          if (status.status === 'completed') {
            addToast('Analysis complete! Your report is ready for review.', 'success');
          } else {
            addToast('Analysis failed. Please check the document and try again.', 'error');
          }
        }
      } catch {
        clearInterval(interval);
        pollingRef.current.delete(docId);
      }
    }, 3_000);
    pollingRef.current.set(docId, interval);
  }, [addToast, refreshNotifications]);

  // ── Auth ───────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    const data = await API.login(email, password);
    setUser(data.user);
    setIsAuthenticated(true);
    setAuthScreen('app');
  }, []);

  const logout = useCallback(() => {
    setAuthScreen('logout');
  }, []);

  const completeLogout = useCallback(() => {
    API.logout();
    setIsAuthenticated(false);
    setUser(null);
    setAuthScreen('login');
    setActiveView('dashboard');
    setDocuments([]);
    setFeedbackItems([]);
    setNotifications([]);
    pollingRef.current.forEach(iv => clearInterval(iv));
    pollingRef.current.clear();
  }, []);

  // ── Filters ────────────────────────────────────────────────────
  const setFilters = useCallback((partial: Partial<FilterState>) => {
    setFiltersState(prev => ({ ...prev, ...partial }));
  }, []);

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (
          !doc.docName.toLowerCase().includes(q) &&
          !doc.fileName.toLowerCase().includes(q) &&
          !doc.id.toLowerCase().includes(q)
        ) return false;
      }
      if (filters.status !== 'all' && doc.status !== filters.status) return false;
      if (filters.quality !== 'all' && doc.qualityRating !== filters.quality) return false;
      return true;
    });
  }, [documents, filters]);

  // ── Navigation ─────────────────────────────────────────────────
  const navigateToDocument = useCallback((documentId: string) => {
    setSelectedDocumentId(documentId);
    setActiveView('analysis');
  }, []);

  // ── Document upload ────────────────────────────────────────────
  const addDocument = useCallback(async (
    file: File,
    docName: string,
    templateVersion: string,
    templateFile?: File,
  ): Promise<string> => {
    const doc = await API.uploadDocument(file, docName, templateVersion, templateFile);
    setDocuments(prev => [doc, ...prev]);
    setShowUploadModal(false);
    addToast(`${doc.fileName} uploaded successfully. Analysis starting…`, 'success');
    startPolling(doc.id);
    return doc.id;
  }, [addToast, startPolling]);

  // ── Document management ───────────────────────────────────────
  const deleteDocument = useCallback((id: string) => {
    API.deleteDocument(id).then(() => {
      setDocuments(prev => prev.filter(d => d.id !== id));
      addToast('Document deleted', 'info');
    }).catch(() => addToast('Failed to delete document', 'error'));
  }, [addToast]);

  const renameDocument = useCallback((id: string, newName: string) => {
    setDocuments(prev => prev.map(d => d.id === id ? { ...d, docName: newName } : d));
    addToast('Document renamed', 'success');
  }, [addToast]);

  const toggleBookmark = useCallback((id: string) => {
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); addToast('Bookmark removed', 'info'); }
      else { next.add(id); addToast('Document bookmarked', 'success'); }
      return next;
    });
  }, [addToast]);

  const archiveDocument = useCallback((id: string) => {
    setArchivedIds(prev => new Set(prev).add(id));
    addToast('Document archived', 'info');
  }, [addToast]);

  // ── Feedback ───────────────────────────────────────────────────
  const updateFeedbackStatus = useCallback((id: string, status: FeedbackStatus) => {
    API.updateFeedbackStatus(id, status).then(updated => {
      setFeedbackItems(prev => prev.map(item => item.id === id ? updated : item));
      addToast(`Feedback updated to "${status.replace('_', ' ')}"`, 'success');
    }).catch(() => addToast('Failed to update feedback', 'error'));
  }, [addToast]);

  const applyAction = useCallback((id: string) => {
    API.applyFeedbackAction(id).then(updated => {
      setFeedbackItems(prev => prev.map(item => item.id === id ? updated : item));
      addToast('Action applied successfully', 'success');
    }).catch(() => addToast('Failed to apply action', 'error'));
  }, [addToast]);

  const dismissFeedback = useCallback((id: string) => {
    API.dismissFeedback(id).then(() => {
      setFeedbackItems(prev => prev.filter(item => item.id !== id));
      addToast('Feedback item dismissed', 'info');
    }).catch(() => addToast('Failed to dismiss feedback', 'error'));
  }, [addToast]);

  // ── Notifications ──────────────────────────────────────────────
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const markNotificationRead = useCallback((id: string) => {
    API.markNotificationRead(id).then(() => {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }).catch(() => { });
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    API.markAllNotificationsRead().then(() => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }).catch(() => { });
  }, []);

  const value: AppState = {
    isAuthenticated, user, authScreen, login, logout, completeLogout,
    activeView, setActiveView,
    sidebarCollapsed, setSidebarCollapsed,
    documents, selectedDocumentId, setSelectedDocumentId, navigateToDocument,
    addDocument, refreshDocuments,
    deleteDocument, renameDocument, toggleBookmark, archiveDocument,
    bookmarkedIds, archivedIds,
    filters, setFilters, filteredDocuments,
    feedbackItems, updateFeedbackStatus, applyAction, dismissFeedback,
    notifications, unreadCount, markNotificationRead, markAllNotificationsRead,
    showNotifications, setShowNotifications,
    showUserMenu, setShowUserMenu,
    toasts, addToast, removeToast,
    showUploadModal, setShowUploadModal,
    backendConnected,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
